import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Tooltip, Typography } from '@mui/material';

import { useTopicStore, useTopicViewerStore } from '@/dmdrive/topic/components/Topic/TopicStore';
import { RotateModel } from '@/dmdrive/topic/components/Topic/TopicStore.constants';

import { ControlMode, ShowModel } from '@carbon/dm-industrial/lib/dm-industrial';

import { LeftSidebarBlock } from '../LeftSidebar.styles';

import { ModelButtons, ModelName, ModelRow } from './ModelsList.styles';

function ModelsList() {
  const { topic, isControlDisabled } = useTopicStore();
  const { viewerSettings, modelToRotate, setModelToRotate } = useTopicViewerStore();

  const { showModel, controlsMode, apply } = viewerSettings;

  // const isSourceVisible = showModel === ShowModel.Source || showModel === ShowModel.Both;
  // const isTargetVisible = showModel === ShowModel.Target || showModel === ShowModel.Both;

  const isSourceVisible = showModel === ShowModel.Source;
  const isTargetVisible = showModel === ShowModel.Target;

  const onSelectPlan = useCallback(() => {
    // if (showModel === ShowModel.Source) {

    //   return;
    // }

    // apply({ showModel: showModel === ShowModel.Both ? ShowModel.Target : ShowModel.Both });

    apply({ showModel: ShowModel.Source });
  }, [isSourceVisible, showModel]);

  const onSelectResult = useCallback(() => {
    // if (showModel === ShowModel.Target) {
    //   return;
    // }

    // apply({ showModel: showModel === ShowModel.Both ? ShowModel.Source : ShowModel.Both });
    apply({ showModel: ShowModel.Target });
  }, [isTargetVisible, showModel]);

  const onSelectModelToMove = useCallback(
    (model: RotateModel) => {
      if (model === modelToRotate && controlsMode === ControlMode.Translate) {
        setModelToRotate(RotateModel.None);
      } else {
        setModelToRotate(model);
        apply({ controlsMode: ControlMode.Translate });
      }
    },
    [modelToRotate, controlsMode],
  );

  const onSelectModelToRotate = useCallback(
    (model: RotateModel) => {
      if (model === modelToRotate && controlsMode === ControlMode.Rotate) {
        setModelToRotate(RotateModel.None);
      } else {
        setModelToRotate(model);
        apply({ controlsMode: ControlMode.Rotate });
      }
    },
    [modelToRotate, controlsMode],
  );

  return (
    <div>
      <LeftSidebarBlock isHeader>
        <Typography variant="h6">Project</Typography>
      </LeftSidebarBlock>

      {topic?.model && (
        <ModelRow>
          <ModelName>
            <IconButton
              size="small"
              color={isSourceVisible ? 'primary' : 'default'}
              onClick={onSelectPlan}
              disableRipple={showModel === ShowModel.Source}
              disabled={isControlDisabled}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>

            <span>Plan</span>
          </ModelName>

          <ModelButtons>
            <Tooltip title="Move model" placement="top">
              <IconButton
                size="small"
                color={
                  modelToRotate === RotateModel.Source && controlsMode === ControlMode.Translate ? 'primary' : undefined
                }
                onClick={() => onSelectModelToMove(RotateModel.Source)}
                disabled={isControlDisabled || !isSourceVisible}
                disableRipple
              >
                <ControlCameraIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Rotate model" placement="top">
              <IconButton
                size="small"
                color={
                  modelToRotate === RotateModel.Source && controlsMode === ControlMode.Rotate ? 'primary' : undefined
                }
                onClick={() => onSelectModelToRotate(RotateModel.Source)}
                disabled={isControlDisabled || !isSourceVisible}
              >
                <Rotate90DegreesCcwIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ModelButtons>
        </ModelRow>
      )}

      {topic?.targetModel && (
        <ModelRow>
          <ModelName>
            <IconButton
              size="small"
              color={isTargetVisible ? 'primary' : 'default'}
              onClick={onSelectResult}
              disableRipple={showModel === ShowModel.Target}
              disabled={isControlDisabled}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>

            <span>Result</span>
          </ModelName>

          <ModelButtons>
            <Tooltip title="Move model" placement="top">
              <IconButton
                size="small"
                color={
                  modelToRotate === RotateModel.Target && controlsMode === ControlMode.Translate ? 'primary' : undefined
                }
                onClick={() => onSelectModelToMove(RotateModel.Target)}
                disabled={isControlDisabled || !isTargetVisible}
              >
                <ControlCameraIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Rotate model" placement="top">
              <IconButton
                size="small"
                color={
                  modelToRotate === RotateModel.Target && controlsMode === ControlMode.Rotate ? 'primary' : undefined
                }
                onClick={() => onSelectModelToRotate(RotateModel.Target)}
                disabled={isControlDisabled || !isTargetVisible}
              >
                <Rotate90DegreesCcwIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ModelButtons>
        </ModelRow>
      )}
    </div>
  );
}

export default observer(ModelsList);
